import React, { useState, useMemo } from 'react';
import { branding } from '../../constants/branding';

interface KYTTransaction {
  transactionHash: string;
  fromAddress: string;
  toAddress: string;
  amount: number;
  blockHeight: number;
  timestamp: string;
  status: string;
  riskScore?: number;
  riskLevel?: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  isSanctioned?: boolean;
  isExchange?: boolean;
  exchangeName?: string;
  gasUsed?: number;
  gasPrice?: number;
  fee?: number;
}

interface KYTTransactionHistoryProps {
  transactions: KYTTransaction[];
  loading?: boolean;
}

type ViewMode = 'table' | 'risk-dashboard';
type SortField = 'timestamp' | 'amount' | 'riskScore' | 'blockHeight';
type SortDirection = 'asc' | 'desc';

interface FilterState {
  riskLevel: string;
  amountRange: { min: number; max: number };
  dateRange: { start: string; end: string };
  searchTerm: string;
  showOnlySanctioned: boolean;
  showOnlyExchanges: boolean;
}

const KYTTransactionHistory: React.FC<KYTTransactionHistoryProps> = ({
  transactions,
  loading = false
}) => {
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [sortField, setSortField] = useState<SortField>('timestamp');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [filters, setFilters] = useState<FilterState>({
    riskLevel: 'all',
    amountRange: { min: 0, max: Number.MAX_SAFE_INTEGER },
    dateRange: { start: '', end: '' },
    searchTerm: '',
    showOnlySanctioned: false,
    showOnlyExchanges: false
  });

  // Utility functions
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 8
    }).format(amount);
  };

  const formatDate = (timestamp: string) => {
    return new Date(timestamp).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRiskLevelColor = (riskLevel?: string) => {
    switch (riskLevel) {
      case 'LOW': return '#4CAF50';
      case 'MEDIUM': return '#FF9800';
      case 'HIGH': return '#F44336';
      case 'CRITICAL': return '#9C27B0';
      default: return '#9E9E9E';
    }
  };

  const getRiskBadgeStyle = (riskLevel?: string) => {
    const color = getRiskLevelColor(riskLevel);
    return {
      backgroundColor: color,
      color: 'white',
      padding: '2px 8px',
      borderRadius: '12px',
      fontSize: '10px',
      fontWeight: '600',
      textTransform: 'uppercase' as const
    };
  };

  // Filter and sort transactions
  const filteredAndSortedTransactions = useMemo(() => {
    let filtered = transactions.filter(tx => {
      // Risk level filter
      if (filters.riskLevel !== 'all' && tx.riskLevel !== filters.riskLevel) {
        return false;
      }

      // Amount range filter
      if (tx.amount < filters.amountRange.min || tx.amount > filters.amountRange.max) {
        return false;
      }

      // Date range filter
      if (filters.dateRange.start) {
        const txDate = new Date(tx.timestamp);
        const startDate = new Date(filters.dateRange.start);
        if (txDate < startDate) return false;
      }
      if (filters.dateRange.end) {
        const txDate = new Date(tx.timestamp);
        const endDate = new Date(filters.dateRange.end);
        if (txDate > endDate) return false;
      }

      // Search term filter
      if (filters.searchTerm) {
        const searchLower = filters.searchTerm.toLowerCase();
        const matchesSearch = 
          tx.transactionHash.toLowerCase().includes(searchLower) ||
          tx.fromAddress.toLowerCase().includes(searchLower) ||
          tx.toAddress.toLowerCase().includes(searchLower) ||
          (tx.exchangeName && tx.exchangeName.toLowerCase().includes(searchLower));
        if (!matchesSearch) return false;
      }

      // Sanctioned filter
      if (filters.showOnlySanctioned && !tx.isSanctioned) {
        return false;
      }

      // Exchange filter
      if (filters.showOnlyExchanges && !tx.isExchange) {
        return false;
      }

      return true;
    });

    // Sort transactions
    filtered.sort((a, b) => {
      let aValue: any, bValue: any;

      switch (sortField) {
        case 'timestamp':
          aValue = new Date(a.timestamp).getTime();
          bValue = new Date(b.timestamp).getTime();
          break;
        case 'amount':
          aValue = Math.abs(a.amount);
          bValue = Math.abs(b.amount);
          break;
        case 'riskScore':
          aValue = a.riskScore || 0;
          bValue = b.riskScore || 0;
          break;
        case 'blockHeight':
          aValue = a.blockHeight;
          bValue = b.blockHeight;
          break;
        default:
          return 0;
      }

      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  }, [transactions, filters, sortField, sortDirection]);

  // Pagination
  const totalPages = Math.ceil(filteredAndSortedTransactions.length / pageSize);
  const paginatedTransactions = filteredAndSortedTransactions.slice(
    currentPage * pageSize,
    (currentPage + 1) * pageSize
  );

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
    setCurrentPage(0);
  };

  const toggleRowExpansion = (txHash: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(txHash)) {
      newExpanded.delete(txHash);
    } else {
      newExpanded.add(txHash);
    }
    setExpandedRows(newExpanded);
  };

  const updateFilter = (updates: Partial<FilterState>) => {
    setFilters(prev => ({ ...prev, ...updates }));
    setCurrentPage(0);
  };

  // Risk Dashboard Statistics
  const riskStats = useMemo(() => {
    const stats = {
      total: transactions.length,
      low: 0,
      medium: 0,
      high: 0,
      critical: 0,
      sanctioned: 0,
      exchanges: 0,
      totalVolume: 0
    };

    transactions.forEach(tx => {
      stats.totalVolume += Math.abs(tx.amount);
      if (tx.isSanctioned) stats.sanctioned++;
      if (tx.isExchange) stats.exchanges++;
      
      switch (tx.riskLevel) {
        case 'LOW': stats.low++; break;
        case 'MEDIUM': stats.medium++; break;
        case 'HIGH': stats.high++; break;
        case 'CRITICAL': stats.critical++; break;
      }
    });

    return stats;
  }, [transactions]);

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        padding: '40px',
        backgroundColor: 'white',
        borderRadius: 12,
        border: `1px solid ${branding.borderColor}`
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: 40,
            height: 40,
            border: '4px solid #f3f3f3',
            borderTop: '4px solid #3498db',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }} />
          <p style={{ color: '#666', margin: 0 }}>Loading transaction history...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      backgroundColor: 'white', 
      borderRadius: 12, 
      border: `1px solid ${branding.borderColor}`,
      padding: 24
    }}>
      {/* Header with View Toggle */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: 24 
      }}>
        <h3 style={{ margin: 0 }}>Transaction History</h3>
        
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={() => setViewMode('table')}
            style={{
              padding: '8px 16px',
              backgroundColor: viewMode === 'table' ? branding.primaryColor : 'transparent',
              color: viewMode === 'table' ? 'white' : branding.textColor,
              border: `1px solid ${branding.primaryColor}`,
              borderRadius: 6,
              cursor: 'pointer',
              fontSize: 14,
              fontWeight: 500
            }}
          >
            📊 Table View
          </button>
          <button
            onClick={() => setViewMode('risk-dashboard')}
            style={{
              padding: '8px 16px',
              backgroundColor: viewMode === 'risk-dashboard' ? branding.primaryColor : 'transparent',
              color: viewMode === 'risk-dashboard' ? 'white' : branding.textColor,
              border: `1px solid ${branding.primaryColor}`,
              borderRadius: 6,
              cursor: 'pointer',
              fontSize: 14,
              fontWeight: 500
            }}
          >
            🚨 Risk Dashboard
          </button>
        </div>
      </div>

      {/* Risk Dashboard View */}
      {viewMode === 'risk-dashboard' && (
        <div>
          {/* Risk Statistics Cards */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
            gap: 16, 
            marginBottom: 24 
          }}>
            <div style={{ 
              padding: 20, 
              backgroundColor: '#f8f9fa', 
              borderRadius: 8, 
              textAlign: 'center',
              border: '1px solid #e9ecef'
            }}>
              <h4 style={{ margin: '0 0 8px 0', fontSize: 14, color: '#666' }}>Total Transactions</h4>
              <div style={{ fontSize: 24, fontWeight: 600, color: branding.primaryColor }}>
                {riskStats.total.toLocaleString()}
              </div>
            </div>

            <div style={{ 
              padding: 20, 
              backgroundColor: '#e8f5e8', 
              borderRadius: 8, 
              textAlign: 'center',
              border: '1px solid #4CAF50'
            }}>
              <h4 style={{ margin: '0 0 8px 0', fontSize: 14, color: '#666' }}>Low Risk</h4>
              <div style={{ fontSize: 24, fontWeight: 600, color: '#4CAF50' }}>
                {riskStats.low}
              </div>
            </div>

            <div style={{ 
              padding: 20, 
              backgroundColor: '#fff3e0', 
              borderRadius: 8, 
              textAlign: 'center',
              border: '1px solid #FF9800'
            }}>
              <h4 style={{ margin: '0 0 8px 0', fontSize: 14, color: '#666' }}>Medium Risk</h4>
              <div style={{ fontSize: 24, fontWeight: 600, color: '#FF9800' }}>
                {riskStats.medium}
              </div>
            </div>

            <div style={{ 
              padding: 20, 
              backgroundColor: '#ffebee', 
              borderRadius: 8, 
              textAlign: 'center',
              border: '1px solid #F44336'
            }}>
              <h4 style={{ margin: '0 0 8px 0', fontSize: 14, color: '#666' }}>High Risk</h4>
              <div style={{ fontSize: 24, fontWeight: 600, color: '#F44336' }}>
                {riskStats.high}
              </div>
            </div>

            <div style={{ 
              padding: 20, 
              backgroundColor: '#f3e5f5', 
              borderRadius: 8, 
              textAlign: 'center',
              border: '1px solid #9C27B0'
            }}>
              <h4 style={{ margin: '0 0 8px 0', fontSize: 14, color: '#666' }}>Critical Risk</h4>
              <div style={{ fontSize: 24, fontWeight: 600, color: '#9C27B0' }}>
                {riskStats.critical}
              </div>
            </div>

            <div style={{ 
              padding: 20, 
              backgroundColor: '#ffebee', 
              borderRadius: 8, 
              textAlign: 'center',
              border: '1px solid #F44336'
            }}>
              <h4 style={{ margin: '0 0 8px 0', fontSize: 14, color: '#666' }}>Sanctioned</h4>
              <div style={{ fontSize: 24, fontWeight: 600, color: '#F44336' }}>
                {riskStats.sanctioned}
              </div>
            </div>

            <div style={{ 
              padding: 20, 
              backgroundColor: '#e3f2fd', 
              borderRadius: 8, 
              textAlign: 'center',
              border: '1px solid #2196F3'
            }}>
              <h4 style={{ margin: '0 0 8px 0', fontSize: 14, color: '#666' }}>Exchanges</h4>
              <div style={{ fontSize: 24, fontWeight: 600, color: '#2196F3' }}>
                {riskStats.exchanges}
              </div>
            </div>

            <div style={{ 
              padding: 20, 
              backgroundColor: '#f8f9fa', 
              borderRadius: 8, 
              textAlign: 'center',
              border: '1px solid #e9ecef'
            }}>
              <h4 style={{ margin: '0 0 8px 0', fontSize: 14, color: '#666' }}>Total Volume</h4>
              <div style={{ fontSize: 18, fontWeight: 600, color: branding.primaryColor }}>
                {formatCurrency(riskStats.totalVolume)}
              </div>
            </div>
          </div>

          {/* High Risk Transactions Alert */}
          {(riskStats.high > 0 || riskStats.critical > 0 || riskStats.sanctioned > 0) && (
            <div style={{ 
              padding: 16, 
              backgroundColor: '#ffebee',
              border: '1px solid #F44336',
              borderRadius: 8,
              marginBottom: 24
            }}>
              <div style={{ color: '#C62828', fontWeight: 600, marginBottom: 8 }}>
                🚨 Risk Alerts Detected
              </div>
              <div style={{ fontSize: 14, color: '#666' }}>
                {riskStats.critical > 0 && `${riskStats.critical} critical risk transactions`}
                {riskStats.critical > 0 && riskStats.high > 0 && ', '}
                {riskStats.high > 0 && `${riskStats.high} high risk transactions`}
                {(riskStats.critical > 0 || riskStats.high > 0) && riskStats.sanctioned > 0 && ', '}
                {riskStats.sanctioned > 0 && `${riskStats.sanctioned} sanctioned addresses`}
              </div>
            </div>
          )}

          {/* Risk Distribution Chart Placeholder */}
          <div style={{ 
            padding: 20, 
            backgroundColor: '#f8f9fa', 
            borderRadius: 8, 
            textAlign: 'center',
            border: '1px solid #e9ecef',
            marginBottom: 24
          }}>
            <h4 style={{ margin: '0 0 12px 0' }}>Risk Distribution</h4>
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 12, height: 12, backgroundColor: '#4CAF50', borderRadius: '50%' }}></div>
                <span>Low ({riskStats.low})</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 12, height: 12, backgroundColor: '#FF9800', borderRadius: '50%' }}></div>
                <span>Medium ({riskStats.medium})</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 12, height: 12, backgroundColor: '#F44336', borderRadius: '50%' }}></div>
                <span>High ({riskStats.high})</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 12, height: 12, backgroundColor: '#9C27B0', borderRadius: '50%' }}></div>
                <span>Critical ({riskStats.critical})</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Table View */}
      {viewMode === 'table' && (
        <div>
          {/* Filters */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
            gap: 16, 
            marginBottom: 24,
            padding: 20,
            backgroundColor: '#f8f9fa',
            borderRadius: 8,
            border: '1px solid #e9ecef'
          }}>
            <div>
              <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 6 }}>
                Search
              </label>
              <input
                type="text"
                placeholder="Hash, addresses, exchange..."
                value={filters.searchTerm}
                onChange={(e) => updateFilter({ searchTerm: e.target.value })}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: 6,
                  fontSize: 14
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 6 }}>
                Risk Level
              </label>
              <select
                value={filters.riskLevel}
                onChange={(e) => updateFilter({ riskLevel: e.target.value })}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: 6,
                  fontSize: 14
                }}
              >
                <option value="all">All Risk Levels</option>
                <option value="LOW">Low Risk</option>
                <option value="MEDIUM">Medium Risk</option>
                <option value="HIGH">High Risk</option>
                <option value="CRITICAL">Critical Risk</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 6 }}>
                From Date
              </label>
              <input
                type="date"
                value={filters.dateRange.start}
                onChange={(e) => updateFilter({ 
                  dateRange: { ...filters.dateRange, start: e.target.value }
                })}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: 6,
                  fontSize: 14
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 6 }}>
                To Date
              </label>
              <input
                type="date"
                value={filters.dateRange.end}
                onChange={(e) => updateFilter({ 
                  dateRange: { ...filters.dateRange, end: e.target.value }
                })}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: 6,
                  fontSize: 14
                }}
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <label style={{ fontSize: 14, fontWeight: 500 }}>
                <input
                  type="checkbox"
                  checked={filters.showOnlySanctioned}
                  onChange={(e) => updateFilter({ showOnlySanctioned: e.target.checked })}
                  style={{ marginRight: 8 }}
                />
                Show only sanctioned
              </label>
              <label style={{ fontSize: 14, fontWeight: 500 }}>
                <input
                  type="checkbox"
                  checked={filters.showOnlyExchanges}
                  onChange={(e) => updateFilter({ showOnlyExchanges: e.target.checked })}
                  style={{ marginRight: 8 }}
                />
                Show only exchanges
              </label>
            </div>
          </div>

          {/* Results Summary */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginBottom: 16 
          }}>
            <div style={{ fontSize: 14, color: '#666' }}>
              Showing {paginatedTransactions.length} of {filteredAndSortedTransactions.length} transactions
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <label style={{ fontSize: 14, color: '#666' }}>Page size:</label>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(0);
                }}
                style={{
                  padding: '4px 8px',
                  border: '1px solid #d1d5db',
                  borderRadius: 4,
                  fontSize: 14
                }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>

          {/* Transaction Table */}
          <div style={{ 
            border: '1px solid #e5e7eb', 
            borderRadius: 8, 
            overflow: 'hidden',
            backgroundColor: 'white'
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ backgroundColor: '#f9fafb' }}>
                <tr>
                  <th 
                    onClick={() => handleSort('timestamp')}
                    style={{
                      padding: '12px 16px',
                      textAlign: 'left',
                      fontSize: 14,
                      fontWeight: 600,
                      color: '#374151',
                      borderBottom: '1px solid #e5e7eb',
                      cursor: 'pointer',
                      userSelect: 'none'
                    }}
                  >
                    Date {sortField === 'timestamp' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th 
                    onClick={() => handleSort('amount')}
                    style={{
                      padding: '12px 16px',
                      textAlign: 'right',
                      fontSize: 14,
                      fontWeight: 600,
                      color: '#374151',
                      borderBottom: '1px solid #e5e7eb',
                      cursor: 'pointer',
                      userSelect: 'none'
                    }}
                  >
                    Amount {sortField === 'amount' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    fontSize: 14,
                    fontWeight: 600,
                    color: '#374151',
                    borderBottom: '1px solid #e5e7eb'
                  }}>
                    From → To
                  </th>
                  <th 
                    onClick={() => handleSort('riskScore')}
                    style={{
                      padding: '12px 16px',
                      textAlign: 'center',
                      fontSize: 14,
                      fontWeight: 600,
                      color: '#374151',
                      borderBottom: '1px solid #e5e7eb',
                      cursor: 'pointer',
                      userSelect: 'none'
                    }}
                  >
                    Risk {sortField === 'riskScore' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th style={{
                    padding: '12px 16px',
                    textAlign: 'center',
                    fontSize: 14,
                    fontWeight: 600,
                    color: '#374151',
                    borderBottom: '1px solid #e5e7eb'
                  }}>
                    Status
                  </th>
                  <th style={{
                    padding: '12px 16px',
                    textAlign: 'center',
                    fontSize: 14,
                    fontWeight: 600,
                    color: '#374151',
                    borderBottom: '1px solid #e5e7eb'
                  }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedTransactions.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ 
                      textAlign: 'center', 
                      padding: '40px', 
                      color: '#6b7280',
                      fontSize: 16
                    }}>
                      No transactions found matching your criteria
                    </td>
                  </tr>
                ) : (
                  paginatedTransactions.map((tx) => (
                    <React.Fragment key={tx.transactionHash}>
                      <tr style={{ 
                        borderBottom: '1px solid #f3f4f6',
                        backgroundColor: expandedRows.has(tx.transactionHash) ? '#f8f9fa' : 'white'
                      }}>
                        <td style={{ padding: '12px 16px', fontSize: 14 }}>
                          {formatDate(tx.timestamp)}
                        </td>
                        <td style={{ 
                          padding: '12px 16px', 
                          textAlign: 'right',
                          fontFamily: 'monospace',
                          fontWeight: 600,
                          color: tx.amount >= 0 ? '#059669' : '#dc2626'
                        }}>
                          {formatCurrency(tx.amount)}
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: 14 }}>
                          <div style={{ marginBottom: 4 }}>
                            <div style={{ 
                              fontFamily: 'monospace', 
                              fontSize: 12, 
                              color: '#6b7280',
                              marginBottom: 2
                            }}>
                              {tx.fromAddress.substring(0, 8)}...{tx.fromAddress.substring(tx.fromAddress.length - 6)}
                            </div>
                            <div style={{ 
                              fontFamily: 'monospace', 
                              fontSize: 12, 
                              color: '#6b7280'
                            }}>
                              {tx.toAddress.substring(0, 8)}...{tx.toAddress.substring(tx.toAddress.length - 6)}
                            </div>
                          </div>
                          {tx.exchangeName && (
                            <div style={{ 
                              fontSize: 11, 
                              color: '#2196F3',
                              fontWeight: 500
                            }}>
                              🏦 {tx.exchangeName}
                            </div>
                          )}
                        </td>
                        <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                          {tx.riskLevel && (
                            <span style={getRiskBadgeStyle(tx.riskLevel)}>
                              {tx.riskLevel}
                            </span>
                          )}
                          {tx.isSanctioned && (
                            <div style={{ 
                              fontSize: 10, 
                              color: '#F44336', 
                              fontWeight: 600,
                              marginTop: 4
                            }}>
                              🚨 SANCTIONED
                            </div>
                          )}
                        </td>
                        <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                          <span style={{
                            padding: '4px 8px',
                            borderRadius: 12,
                            fontSize: 11,
                            fontWeight: 600,
                            backgroundColor: tx.status === 'SUCCESS' ? '#e8f5e8' : '#ffebee',
                            color: tx.status === 'SUCCESS' ? '#2e7d32' : '#c62828'
                          }}>
                            {tx.status}
                          </span>
                        </td>
                        <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                          <button
                            onClick={() => toggleRowExpansion(tx.transactionHash)}
                            style={{
                              padding: '4px 8px',
                              backgroundColor: 'transparent',
                              border: '1px solid #d1d5db',
                              borderRadius: 4,
                              cursor: 'pointer',
                              fontSize: 12,
                              color: '#374151'
                            }}
                          >
                            {expandedRows.has(tx.transactionHash) ? '▼' : '▶'} Details
                          </button>
                        </td>
                      </tr>
                      
                      {/* Expanded Row Details */}
                      {expandedRows.has(tx.transactionHash) && (
                        <tr style={{ backgroundColor: '#f8f9fa' }}>
                          <td colSpan={6} style={{ padding: '16px' }}>
                            <div style={{ 
                              display: 'grid', 
                              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                              gap: 16,
                              fontSize: 14
                            }}>
                              <div>
                                <strong>Transaction Hash:</strong>
                                <div style={{ 
                                  fontFamily: 'monospace', 
                                  fontSize: 12, 
                                  color: '#6b7280',
                                  wordBreak: 'break-all',
                                  marginTop: 4
                                }}>
                                  {tx.transactionHash}
                                </div>
                              </div>
                              <div>
                                <strong>Block Height:</strong>
                                <div style={{ marginTop: 4 }}>{tx.blockHeight.toLocaleString()}</div>
                              </div>
                              <div>
                                <strong>From Address:</strong>
                                <div style={{ 
                                  fontFamily: 'monospace', 
                                  fontSize: 12, 
                                  color: '#6b7280',
                                  wordBreak: 'break-all',
                                  marginTop: 4
                                }}>
                                  {tx.fromAddress}
                                </div>
                              </div>
                              <div>
                                <strong>To Address:</strong>
                                <div style={{ 
                                  fontFamily: 'monospace', 
                                  fontSize: 12, 
                                  color: '#6b7280',
                                  wordBreak: 'break-all',
                                  marginTop: 4
                                }}>
                                  {tx.toAddress}
                                </div>
                              </div>
                              {tx.gasUsed && (
                                <div>
                                  <strong>Gas Used:</strong>
                                  <div style={{ marginTop: 4 }}>{tx.gasUsed.toLocaleString()}</div>
                                </div>
                              )}
                              {tx.gasPrice && (
                                <div>
                                  <strong>Gas Price:</strong>
                                  <div style={{ marginTop: 4 }}>{tx.gasPrice} Gwei</div>
                                </div>
                              )}
                              {tx.fee && (
                                <div>
                                  <strong>Transaction Fee:</strong>
                                  <div style={{ marginTop: 4 }}>{formatCurrency(tx.fee)}</div>
                                </div>
                              )}
                              {tx.riskScore && (
                                <div>
                                  <strong>Risk Score:</strong>
                                  <div style={{ 
                                    marginTop: 4,
                                    color: getRiskLevelColor(tx.riskLevel),
                                    fontWeight: 600
                                  }}>
                                    {tx.riskScore}%
                                  </div>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              gap: 8, 
              marginTop: 24 
            }}>
              <button
                onClick={() => setCurrentPage(0)}
                disabled={currentPage === 0}
                style={{
                  padding: '8px 12px',
                  backgroundColor: currentPage === 0 ? '#f3f4f6' : 'white',
                  border: '1px solid #d1d5db',
                  borderRadius: 6,
                  cursor: currentPage === 0 ? 'not-allowed' : 'pointer',
                  fontSize: 14,
                  color: currentPage === 0 ? '#9ca3af' : '#374151'
                }}
              >
                First
              </button>
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 0}
                style={{
                  padding: '8px 12px',
                  backgroundColor: currentPage === 0 ? '#f3f4f6' : 'white',
                  border: '1px solid #d1d5db',
                  borderRadius: 6,
                  cursor: currentPage === 0 ? 'not-allowed' : 'pointer',
                  fontSize: 14,
                  color: currentPage === 0 ? '#9ca3af' : '#374151'
                }}
              >
                Previous
              </button>
              
              <span style={{ 
                padding: '8px 12px',
                backgroundColor: branding.primaryColor,
                color: 'white',
                borderRadius: 6,
                fontSize: 14,
                fontWeight: 600
              }}>
                {currentPage + 1} of {totalPages}
              </span>
              
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage >= totalPages - 1}
                style={{
                  padding: '8px 12px',
                  backgroundColor: currentPage >= totalPages - 1 ? '#f3f4f6' : 'white',
                  border: '1px solid #d1d5db',
                  borderRadius: 6,
                  cursor: currentPage >= totalPages - 1 ? 'not-allowed' : 'pointer',
                  fontSize: 14,
                  color: currentPage >= totalPages - 1 ? '#9ca3af' : '#374151'
                }}
              >
                Next
              </button>
              <button
                onClick={() => setCurrentPage(totalPages - 1)}
                disabled={currentPage >= totalPages - 1}
                style={{
                  padding: '8px 12px',
                  backgroundColor: currentPage >= totalPages - 1 ? '#f3f4f6' : 'white',
                  border: '1px solid #d1d5db',
                  borderRadius: 6,
                  cursor: currentPage >= totalPages - 1 ? 'not-allowed' : 'pointer',
                  fontSize: 14,
                  color: currentPage >= totalPages - 1 ? '#9ca3af' : '#374151'
                }}
              >
                Last
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default KYTTransactionHistory;
